import { isVideoItem } from './items';

// Video wall playback runs on an absolute timeline: given when a slot started, the item showing
// and how far into it we are follow from the clock alone, so every tile (including one that
// boots late) lands on the same frame without talking to the others.

const DEFAULT_SECONDS = 10;

/**
 * Seconds an item lasts on a wall. Fixed, never "until the video ends": an item's own duration,
 * else a video's natural length, else the playlist's default.
 */
export const wallItemSeconds = (item, playlist) => {
  if (item?.duration > 0) return item.duration;
  const natural = item?.file?.durationSeconds;
  if (isVideoItem(item) && natural > 0) return natural;
  return playlist?.duration > 0 ? playlist.duration : DEFAULT_SECONDS;
};

/**
 * Where a looping list of items (durations in ms) is at `nowMs`, having started at `anchorMs`:
 * `{ index, itemStartMs, nextChangeAtMs }`. A single item never changes (nextChangeAtMs is
 * Infinity and it started at the anchor), so it is never remounted. Null for an empty list.
 */
export const timelineAt = (durationsMs, anchorMs, nowMs) => {
  const durations = (durationsMs || []).map((d) =>
    d > 0 ? d : DEFAULT_SECONDS * 1000,
  );
  if (!durations.length) return null;
  if (durations.length === 1)
    return { index: 0, itemStartMs: anchorMs, nextChangeAtMs: Infinity };

  const total = durations.reduce((sum, d) => sum + d, 0);
  const elapsed = Math.max(nowMs - anchorMs, 0);
  const cycleStart = anchorMs + Math.floor(elapsed / total) * total;

  let start = cycleStart;
  for (let index = 0; index < durations.length; index++) {
    const end = start + durations[index];
    if (nowMs < end) return { index, itemStartMs: start, nextChangeAtMs: end };
    start = end;
  }
  // Floating-point edge at the very end of a cycle: the next cycle's first item.
  return { index: 0, itemStartMs: start, nextChangeAtMs: start + durations[0] };
};
